import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity, Modal, ScrollView } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores, fuentes } from '../../styles/variablesCss';
import PagoTarjetaCredito from './FormasDePago/PagoTarjetaCredito';
import PagoTarjetaDebito from './FormasDePago/PagoTarjetaDebito';
import PagoCuentaEmpleado from './FormasDePago/PagoCuentaEmpleado';
import PagoBilleteraStaFe from './FormasDePago/PagoBilleteraStaFe';
import { BACKEND_URL } from '../../Config/Const';

import { useCheckoutContext } from '../../context/CheckoutContext';
import { useUser } from '../../context/UserContext';

const FormasDePago = ({ route = { params: { total: 0 } } }) => {
  // Estados locales
  const [formaPago, setFormaPago] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [mostrarFormaPago, setMostrarFormaPago] = useState(false);
  const [mostrarDebitoPago, setMostrarDebitoPago] = useState(false);
  const [esEmpleado, setEsEmpleado] = useState(false);
  const [verificandoEmpleado, setVerificandoEmpleado] = useState(true);
  const [nroEmpresa, setNroEmpresa] = useState(null);

  // Ref para el select
  const selectRef = useRef(null);

  // Obtener datos del usuario
  const { mail, codUsu } = useUser();

  // Todas las opciones de pago disponibles
  const todasLasOpcionesPago = [
    { value: '', label: 'Seleccioná tu forma de pago', icon: 'cash-multiple' },
    { value: 'tarjeta_credito', label: 'Tarjeta de Crédito', icon: 'credit-card' },
    { value: 'tarjeta_debito', label: 'Tarjeta de Débito', icon: 'credit-card-outline' },
    { value: 'cuenta_empleado', label: nroEmpresa === 999  ? 'Cuenta Empleado/Directivo' : 'Cuenta Afinidad', icon: 'account-cash' },
    { value: 'billetera_santa_fe', label: 'Billetera Santa Fe', icon: 'wallet' },
    { value: 'modo_app', label: 'MODO', icon: 'cellphone-wireless' },
  ];

  // Filtrar opciones según si es empleado o no
  const opcionesPago = esEmpleado
    ? todasLasOpcionesPago
    : todasLasOpcionesPago.filter(opcion => opcion.value !== 'cuenta_empleado');

  // Obtener la opción seleccionada actualmente
  const opcionSeleccionada = opcionesPago.find(opcion => opcion.value === formaPago) || opcionesPago[0];

  // Obtener el contexto de checkout
  const { setTipoPago } = useCheckoutContext();

  // Verificar si el usuario es empleado al montar el componente
  useEffect(() => {
    const verificarEmpleado = async () => {
      try {
        let emailUsuario = mail;
        let codigoUsuario = codUsu;

        // Obtener datos de autenticación si no están en el contexto
        if (!emailUsuario || !codigoUsuario) {
          if (Platform.OS === 'web') {
            emailUsuario = localStorage.getItem('mail') || '';
            codigoUsuario = localStorage.getItem('codUsu') || '';
          }
        }

        // Si no hay datos de usuario, no es empleado
        if (!emailUsuario || !codigoUsuario) {
          setEsEmpleado(false);
          setVerificandoEmpleado(false);
          return;
        }

        // Crear query params
        const queryParams = new URLSearchParams();
        queryParams.append('mail', emailUsuario);
        queryParams.append('codUsu', codigoUsuario);

        // Consultar API de saldo de cuenta
        const response = await fetch(`${BACKEND_URL}/rest/fin/saldoCuenta?${queryParams.toString()}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        // Extraer nroEmpresa de la respuesta
        const saldoCuentaData = data?.response?.dsResultado?.dsResultado?.saldoCuenta?.[0];
        const empresaNumero = saldoCuentaData?.nroEmpresa;
        
        if (empresaNumero) {
          setNroEmpresa(empresaNumero);
        }

        // Verificar si el usuario es empleado según la respuesta
        if (data?.response?.dsResultado?.dsResultado?.resultado?.[0]?.resultado === true) {
          setEsEmpleado(true);
        } else {
          setEsEmpleado(false);
        }
      } catch (error) {
        // Si hay error, asumir que no es empleado
        setEsEmpleado(false);
      } finally {
        setVerificandoEmpleado(false);
      }
    };

    verificarEmpleado();
  }, [mail, codUsu]);

  // Efecto para abrir automáticamente el select al montar el componente
  useEffect(() => {
    if (Platform.OS === 'web' && selectRef.current && !verificandoEmpleado) {
      // Pequeño delay para asegurar que el DOM esté listo
      const timer = setTimeout(() => {
        try {
          selectRef.current?.focus();
          // Simular click para abrir el dropdown
          if (selectRef.current && typeof selectRef.current.showPicker === 'function') {
            selectRef.current.showPicker();
          }
        } catch (error) {
          // Ignorar errores si el navegador no soporta showPicker
          //console.log('showPicker no disponible');
        }
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [verificandoEmpleado]);

  // Efecto para actualizar estados cuando cambia la forma de pago
  useEffect(() => {
    setMostrarFormaPago(formaPago === 'tarjeta_credito');
    setMostrarDebitoPago(formaPago === 'tarjeta_debito');
    
    // Actualizar el tipo de pago según la selección
    switch (formaPago) {
      case 'tarjeta_credito':
        setTipoPago('1'); // Código para tarjeta de crédito
        break;
      case 'tarjeta_debito':
        setTipoPago('2'); // Código para tarjeta de débito
        break;
      case 'cuenta_empleado':
        setTipoPago('3'); // Código para cuenta empleado
        break;
      case 'billetera_santa_fe':
        setTipoPago('4'); // Código para billetera Santa Fe
        break;
      case 'modo_app':
        setTipoPago('5'); // Código para MODO o app bancarias
        break;
      default:
        setTipoPago('0');
        break;
    }
  }, [formaPago, setTipoPago]);

  // Función para renderizar el selector de método de pago
  const renderizarSelector = () => {
      // En web, usamos un elemento select nativo
      if (Platform.OS === 'web') {
        return (
          <View style={styles.selectorContainer}>
            <select
              ref={selectRef}
              style={styles.selectWeb}
              value={formaPago}
              onChange={(e) => setFormaPago(e.target.value)}
            >
              {opcionesPago.map((opcion) => (
                <option key={opcion.value} value={opcion.value}>
                  {opcion.label}
                </option>
              ))}
            </select>
            <MaterialCommunityIcons
              name="chevron-down"
              size={20}
              color={colores.textoPrincipal}
              style={styles.selectIcon}
            />
          </View>
        );
      }

      // En móvil, usamos un componente personalizado que abre un modal
      return (
        <>
          <TouchableOpacity
            style={styles.selectorTouchable}
            onPress={() => setModalVisible(true)}
          >
            <MaterialCommunityIcons
              name={opcionSeleccionada.icon}
              size={24}
              color={colores.rojo}
              style={styles.opcionIcono}
            />
            <Text
              style={styles.opcionTexto}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {opcionSeleccionada.label}
            </Text>
            <MaterialCommunityIcons
              name="chevron-down"
              size={20}
              color={colores.rojo}
              style={styles.iconoDropdown}
            />
          </TouchableOpacity>

          <Modal
            visible={modalVisible}
            transparent={true}
            animationType="slide"
            onRequestClose={() => setModalVisible(false)}
          >
            <View style={styles.modalContainer}>
              <View style={styles.modalContent}>
                <Text style={styles.modalTitle}>Seleccionar método de pago</Text>

                <ScrollView style={styles.opcionesList}>
                  {opcionesPago.map((opcion) =>
                    opcion.value ? (
                      <TouchableOpacity
                        key={opcion.value}
                        style={[styles.opcionItem, formaPago === opcion.value && styles.opcionSeleccionada]}
                        onPress={() => {
                          setFormaPago(opcion.value);
                          setModalVisible(false);
                        }}
                      >
                        <MaterialCommunityIcons
                          name={opcion.icon}
                          size={24}
                          color={formaPago === opcion.value ? colores.rojo : colores.textoPrincipal}
                          style={styles.opcionIcono}
                        />
                        <Text style={[styles.opcionItemText, formaPago === opcion.value && styles.opcionTextSeleccionado]}>
                          {opcion.label}
                        </Text>
                      </TouchableOpacity>
                    ) : null
                  )}
                </ScrollView>

                <TouchableOpacity
                  style={styles.cancelarButton}
                  onPress={() => setModalVisible(false)}
                >
                  <Text style={styles.cancelarButtonText}>Cancelar</Text>
                </TouchableOpacity>
              </View>
            </View>
          </Modal>
        </>
      );
  };

  return (
    <View style={styles.container}>
      {renderizarSelector()}

      {formaPago === 'cuenta_empleado' && (
        <View style={styles.componenteContainer}>
          <PagoCuentaEmpleado
            total={route?.params?.total || 0}
            nroEmpresa={nroEmpresa}
          />
        </View>
      )}

      {/* No mostrar componente para billetera_santa_fe, solo actualizar tipoPago a 4 */}
      {formaPago === 'billetera_santa_fe' && (
        <View style={styles.infoAdicional}>
          <Text style={styles.infoText}>Método de pago: Billetera Santa Fe</Text>
        </View>
      )}

      {formaPago === 'modo_app' && (
        <View style={styles.infoAdicional}>
          <Text style={styles.infoText}>Método de pago: MODO</Text>
        </View>
      )}

      {/* Mostrar componente para tarjeta de crédito */}
      {mostrarFormaPago && (
        <PagoTarjetaCredito
          route={route}
          mostrarFormasDePagoExterno={true}
          formaPagoSeleccionadaExterna={'tarjeta_credito'}
        />
      )}

      {/* Mostrar componente para tarjeta de débito */}
      {mostrarDebitoPago && (
        <PagoTarjetaDebito
          total={route?.params?.total || 0}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
    container: {
      width: '100%',
      marginVertical: 10,
    },
    componenteContainer: {
      width: '100%',
      marginVertical: 10,
    },
    metodosPagoTitulo: {
      fontSize: 16,
      fontFamily: fuentes.semiBold,
      color: colores.textoPrincipal,
      marginBottom: 10,
    },
    selectorContainer: {
      position: 'relative',
      marginBottom: 15,
    },
    selectWeb: {
      width: '100%',
      padding: 12,
      fontSize: 16,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: colores.borde,
      backgroundColor: colores.blanco,
      color: colores.textoPrincipal,
      appearance: 'none',
    },
    selectIcon: {
      position: 'absolute',
      right: 15,
      top: 12,
      pointerEvents: 'none',
    },
    selectorTouchable: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 12,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: colores.borde,
      backgroundColor: colores.blanco,
      marginBottom: 15,
    },
    opcionIcono: {
      marginRight: 8,
    },
    opcionTexto: {
      flex: 1,
      color: colores.textoPrincipal,
      fontSize: 16,
    },
    iconoDropdown: {
      marginLeft: 8,
    },
    modalContainer: {
      flex: 1,
      justifyContent: 'flex-end',
    },
    modalContent: {
      backgroundColor: colores.blanco,
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
      padding: 20,
      minHeight: 300,
      maxHeight: '80%',
    },
    modalTitle: {
      fontSize: 18,
      fontFamily: fuentes.semiBold,
      color: colores.textoPrincipal,
      textAlign: 'center',
      marginBottom: 20,
    },
    opcionesList: {
      marginBottom: 20,
    },
    opcionItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 15,
      borderBottomWidth: 1,
      borderBottomColor: colores.borde,
    },
    opcionSeleccionada: {
      backgroundColor: 'rgba(231, 29, 54, 0.05)',
    },
    opcionIcono: {
      marginRight: 15,
    },
    // Estilos para el formulario de tarjeta
    datosTarjetaContainer: {
      marginTop: 10,
      width: '100%',
    },
    datosTarjetaInput: {
      backgroundColor: colores.fondoClaro,
      borderWidth: 1,
      borderColor: colores.borde,
      borderRadius: 8,
      padding: 12,
      fontSize: 14,
      fontFamily: fuentes.regular,
      color: colores.textoPrincipal,
      marginBottom: 10,
    },
    datosTarjetaRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
    },
    opcionItemText: {
      fontSize: 16,
      color: colores.textoPrincipal,
      fontFamily: fuentes.regular,
    },
    opcionTextSeleccionado: {
      fontFamily: fuentes.semiBold,
      color: colores.rojo,
    },
    cancelarButton: {
      paddingVertical: 15,
      borderRadius: 8,
      alignItems: 'center',
      backgroundColor: colores.fondoClaro,
    },
    cancelarButtonText: {
      fontSize: 16,
      color: colores.rojo,
      fontFamily: fuentes.semiBold,
    },
    infoAdicional: {
      padding: 10,
      backgroundColor: colores.fondoClaro,
      borderRadius: 6,
      marginBottom: 15,
    },
    infoText: {
      color: colores.textoPrincipal,
      fontSize: 14,
    },
  });

  export default FormasDePago;
